import React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Shape,
  DrawingTool,
  Point,
  ImageScale,
  DrawingState,
  PixelSpacing,
  FreehandShape,
} from '../types';
import {
  FETAL_ANNOTATION_LABELS,
  LABEL_CATEGORIES,
} from '../constants/fetalLabels';
import { getMeasurementDisplayName } from '../../../../constants/measurementLabels';
import {
  canvasToImageCoordinates,
  imageToCanvasCoordinates,
  getShapeColor,
  calculateLineLength,
  calculateRectangleArea,
  calculateRectanglePerimeter,
  calculateEllipsePerimeter,
  calculateEllipseArea,
  calculatePolygonPerimeter,
  calculatePolylineLength,
  calculatePolygonArea,
  formatMeasurement,
  isPointInShape,
  moveShape,
  getShapeControlPoints,
  findControlHandleAt,
  updateShapeHandle,
  isFreehandShapeClosed,
  simplifyFreehandPoints,
  buildSmoothFreehandPath,
  buildSmoothFreehandCanvasPolyline,
} from '../utils/shapeUtils';

interface ImageCanvasProps {
  width?: number;
  height?: number;
  selectedTool: DrawingTool;
  shapes: Shape[];
  pixelSpacing?: PixelSpacing; // Optional: undefined means use pixels as unit
  pixel_spacing_unit?: string; // Optional: unit of pixel spacing (e.g., 'cm', 'mm')
  onShapeAdd?: (shape: Omit<Shape, 'id'>) => void;
  onShapeUpdate?: (id: string, updates: Partial<Shape>) => void;
  onShapeSelect?: (id: string | null) => void;
  onShapeDelete?: (id: string) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  zoomLevel?: number;
  onZoomChange?: (zoomLevel: number) => void;
  annotationsVisible?: boolean;
  imageSrc: string;
  onNextImage?: () => void;
  onPreviousImage?: () => void;
  isEditMode?: boolean; // Whether edit mode is active (allows drawing/editing)
  presetLabel?: string | null; // Preset label to apply to newly drawn shapes
  presetColor?: string | null; // Preset color to apply to newly drawn shapes
  showLabels?: boolean; // Whether to show labels on shapes
  hoveredShapeId?: string | null;
  onHoverChange?: (id: string | null) => void;
  /** Category IDs to hide (match by shape.categoryId so e.g. Abdominal Biometry and Meas AC share same id) */
  hiddenCategoryIds?: number[];
  /** When true, the video element is shown behind the canvas; canvas draws only shapes (no image). */
  videoMode?: boolean;
  /** Intrinsic video size for scale/coordinates when videoMode is true. */
  videoDimensions?: { width: number; height: number } | null;
  /** When set, receives the drawing canvas element (for export / screenshots). */
  exportCanvasRef?: React.MutableRefObject<HTMLCanvasElement | null>;
}

export function ImageCanvas({
  width = 800,
  height = 600,
  selectedTool,
  shapes,
  pixelSpacing,
  pixel_spacing_unit,
  onShapeAdd,
  onShapeUpdate,
  onShapeSelect,
  onShapeDelete,
  onDragStart,
  onDragEnd,
  zoomLevel = 1,
  onZoomChange,
  annotationsVisible = true,
  imageSrc,
  onNextImage,
  onPreviousImage,
  isEditMode = true,
  presetLabel = null,
  presetColor = null,
  showLabels = true,
  hiddenCategoryIds = [],
  videoMode = false,
  videoDimensions = null,
  exportCanvasRef,
}: ImageCanvasProps) {
  const { t } = useTranslation(['GeneralViewer']);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const setCanvasRef = (el: HTMLCanvasElement | null) => {
    (canvasRef as React.MutableRefObject<HTMLCanvasElement | null>).current = el;
    if (exportCanvasRef) exportCanvasRef.current = el;
  };
  const containerRef = useRef<HTMLDivElement>(null);
  /** Container size (wrapper div) for video mode so we can match object-contain content rect. */
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  /** When in video mode, canvas is sized to the video's displayed content rect (same as object-contain) for exact overlay. */
  const [videoContentSize, setVideoContentSize] = useState<{ width: number; height: number } | null>(null);
  const [imageScale, setImageScale] = useState<ImageScale>({
    scaleX: 1,
    scaleY: 1,
    offsetX: 0,
    offsetY: 0,
    imageWidth: 0,
    imageHeight: 0,
    zoomLevel: 1,
    panX: 0,
    panY: 0,
  });
  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
  });
  const [currentMousePos, setCurrentMousePos] = useState<Point | null>(null);
  const [rulerState, setRulerState] = useState<{
    isMeasuring: boolean;
    startPoint?: Point;
    endPoint?: Point;
  }>({
    isMeasuring: false,
  });
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    draggedShapeId: string | null;
    lastMousePos: Point | null;
  }>({
    isDragging: false,
    draggedShapeId: null,
    lastMousePos: null,
  });

  const [editState, setEditState] = useState<{
    isEditing: boolean;
    editingShapeId: string | null;
    editingHandle: string | null;
  }>({
    isEditing: false,
    editingShapeId: null,
    editingHandle: null,
  });

  const [panState, setPanState] = useState<{
    isPanning: boolean;
    lastPanPos: Point | null;
  }>({
    isPanning: false,
    lastPanPos: null,
  });

  const [hoverState, setHoverState] = useState<{
    hoveredShapeId: string | null;
    hoveredHandle: string | null;
  }>({
    hoveredShapeId: null,
    hoveredHandle: null,
  });

  // Touch-specific state for long-press vertex editing
  const [touchEditState, setTouchEditState] = useState<{
    longPressTimer: NodeJS.Timeout | null;
    touchStartPos: Point | null;
    potentialHandle: { shapeId: string; handleName: string } | null;
    isLongPressActive: boolean;
  }>({
    longPressTimer: null,
    touchStartPos: null,
    potentialHandle: null,
    isLongPressActive: false,
  });

  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    shapeId: string | null;
    showLabelMenu: boolean;
  }>({
    visible: false,
    x: 0,
    y: 0,
    shapeId: null,
    showLabelMenu: false,
  });

  // Store the loaded image
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

  // Track actual canvas size (for responsive sizing)
  const [canvasSize, setCanvasSize] = useState({ width, height });

  // Cycle through overlapping shapes on delete (e.g. Heart under Abdo)
  const lastDeletePointRef = useRef<Point | null>(null);
  const deleteCycleIndexRef = useRef(0);
  const DELETE_CYCLE_TOLERANCE = 15;

  // Pan: ref so first mousemove sees it before React state updates (pan must only move view, never shapes)
  const panningRef = useRef<{ lastPanPos: Point } | null>(null);

  // In video mode: observe container and size canvas to video content rect (object-contain). Otherwise: observe canvas.
  useEffect(() => {
    if (videoMode && containerRef.current) {
      const container = containerRef.current;
      const updateContainerSize = () => {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const w = Math.round(rect.width);
          const h = Math.round(rect.height);
          setContainerSize((prev) => (prev?.width === w && prev?.height === h ? prev : { width: w, height: h }));
        }
      };
      updateContainerSize();
      const ro = new ResizeObserver(updateContainerSize);
      ro.observe(container);
      return () => ro.disconnect();
    }
    setContainerSize(null);
    setVideoContentSize(null);
    return () => {};
  }, [videoMode]);

  // When video mode: compute canvas size to match video's displayed content (same as object-contain) and set canvas size.
  useEffect(() => {
    if (!videoMode || !containerSize || !videoDimensions?.width || !videoDimensions?.height) return;
    const vw = videoDimensions.width;
    const vh = videoDimensions.height;
    const scale = Math.min(containerSize.width / vw, containerSize.height / vh);
    const contentW = Math.round(vw * scale);
    const contentH = Math.round(vh * scale);
    if (contentW <= 0 || contentH <= 0) return;
    setVideoContentSize({ width: contentW, height: contentH });
    setCanvasSize({ width: contentW, height: contentH });
  }, [videoMode, containerSize, videoDimensions?.width, videoDimensions?.height]);

  // Resize canvas to match its container display size (image mode only; video mode size is driven above)
  useEffect(() => {
    if (videoMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const newWidth = Math.round(rect.width);
        const newHeight = Math.round(rect.height);
        if (newWidth !== canvasSize.width || newHeight !== canvasSize.height) {
          setCanvasSize({ width: newWidth, height: newHeight });
        }
      }
    };

    updateCanvasSize();
    const resizeObserver = new ResizeObserver(updateCanvasSize);
    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, [videoMode, canvasSize.width, canvasSize.height]);

  // When videoMode, use video dimensions for scale (no image load); otherwise load image and compute scale.
  const TRANSPARENT_1X1 = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  useEffect(() => {
    const canvas = canvasRef.current;
    const actualWidth = canvasSize.width || (canvas?.width ?? width);
    const actualHeight = canvasSize.height || (canvas?.height ?? height);

    // Skip until the layout has a real size — otherwise offsets become negative and the image sticks left.
    if (actualWidth <= 1 || actualHeight <= 1) {
      return;
    }

    if (videoMode && videoDimensions && videoDimensions.width > 0 && videoDimensions.height > 0) {
      const vw = videoDimensions.width;
      const vh = videoDimensions.height;
      // Canvas is sized to video content rect (object-contain), so 1:1 mapping with no letterbox offset
      const baseScale = actualWidth / vw;
      setImageScale({
        scaleX: baseScale,
        scaleY: baseScale,
        offsetX: 0,
        offsetY: 0,
        imageWidth: vw,
        imageHeight: vh,
        zoomLevel: zoomLevel,
        panX: 0,
        panY: 0,
      });
      const img = new Image();
      img.onload = () => setLoadedImage(img);
      img.src = TRANSPARENT_1X1;
      return;
    }

    const img = new Image();
    img.onload = () => {
      setLoadedImage(img);
      const w = canvasRef.current?.width || canvasSize.width || width;
      const h = canvasRef.current?.height || canvasSize.height || height;
      if (w <= 1 || h <= 1 || !img.width || !img.height) return;
      const scaleX = w / img.width;
      const scaleY = h / img.height;
      const baseScale = Math.min(scaleX, scaleY);
      const effectiveScale = baseScale * zoomLevel;
      const scaledWidth = img.width * effectiveScale;
      const scaledHeight = img.height * effectiveScale;
      const offsetX = (w - scaledWidth) / 2;
      const offsetY = (h - scaledHeight) / 2;
      setImageScale({
        scaleX: baseScale,
        scaleY: baseScale,
        offsetX,
        offsetY,
        imageWidth: img.width,
        imageHeight: img.height,
        zoomLevel: zoomLevel,
        // Always re-center on layout / zoom changes (avoids stuck left after panel resize).
        panX: 0,
        panY: 0,
      });
    };
    img.src = imageSrc;
  }, [imageSrc, zoomLevel, canvasSize.width, canvasSize.height, videoMode, videoDimensions?.width, videoDimensions?.height, width, height]);

  // Draw all shapes on canvas
const drawShapes = useCallback(
  (ctx: CanvasRenderingContext2D) => {
    if (!annotationsVisible) return;
 
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
 
    const canvasW = ctx.canvas.width;
    const canvasH = ctx.canvas.height;
 
    // ── Collision helpers ─────────────────────────────────────────────────
 
    const placedLabels: Array<{ x: number; y: number; w: number; h: number }> = [];
 
    function overlapArea(
      ax: number, ay: number, aw: number, ah: number,
      bx: number, by: number, bw: number, bh: number
    ): number {
      const ox = Math.max(0, Math.min(ax + aw, bx + bw) - Math.max(ax, bx));
      const oy = Math.max(0, Math.min(ay + ah, by + bh) - Math.max(ay, by));
      return ox * oy;
    }
 
    function totalOverlap(x: number, y: number, w: number, h: number): number {
      return placedLabels.reduce(
        (sum, r) => sum + overlapArea(x, y, w, h, r.x, r.y, r.w, r.h),
        0
      );
    }
 
    function findBestLabelPosition(
      anchorX: number,
      anchorY: number,
      labelW: number,
      labelH: number,
      leaderLength: number = 40
    ): { lx: number; ly: number } {
      const L = leaderLength;
      const D = L * 0.72;
 
      const candidates: Array<[number, number]> = [
        [anchorX + L,          anchorY - labelH / 2],
        [anchorX - L - labelW, anchorY - labelH / 2],
        [anchorX - labelW / 2, anchorY - L - labelH],
        [anchorX - labelW / 2, anchorY + L],
        [anchorX + D,          anchorY - D - labelH],
        [anchorX - D - labelW, anchorY - D - labelH],
        [anchorX + D,          anchorY + D],
        [anchorX - D - labelW, anchorY + D],
      ];
 
      const MARGIN = 6;
      let bestX = candidates[0][0];
      let bestY = candidates[0][1];
      let bestScore = Infinity;
 
      for (const [cx, cy] of candidates) {
        const clampedX = Math.max(MARGIN, Math.min(canvasW - labelW - MARGIN, cx));
        const clampedY = Math.max(MARGIN, Math.min(canvasH - labelH - MARGIN, cy));
        const score = totalOverlap(clampedX, clampedY, labelW, labelH);
 
        if (score < bestScore) {
          bestScore = score;
          bestX = clampedX;
          bestY = clampedY;
        }
        if (score === 0) break;
      }
 
      return { lx: bestX, ly: bestY };
    }
 
    // ── Anchor dot ────────────────────────────────────────────────────────
 
    function drawAnchorDot(
      x: number,
      y: number,
      color: string,
      isActive: boolean
    ) {
      ctx.save();
      ctx.setLineDash([]);
 
      if (isActive) {
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fillStyle = `${color}28`;
        ctx.fill();
      }
 
      ctx.beginPath();
      ctx.arc(x, y, isActive ? 5 : 3.5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = isActive ? 1.0 : 0.55;
      ctx.fill();
 
      ctx.beginPath();
      ctx.arc(x, y, isActive ? 2.5 : 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = isActive ? 1.0 : 0.80;
      ctx.fill();
 
      ctx.restore();
    }
 
    // ── Pill label ────────────────────────────────────────────────────────
 
    function drawSmartLabel(
      anchorX: number,
      anchorY: number,
      nameText: string,
      valueText: string,
      isManualShape: boolean | null,
      color: string
    ) {
      if (!nameText && !valueText) return;
 
      ctx.save();
      ctx.globalAlpha = 1.0;
 
      const PADDING_X = 9;
      const PADDING_Y = 5;
      const NAME_FONT  = '500 10px Inter, sans-serif';
      const VALUE_FONT = 'bold 12px Inter, sans-serif';
      const LINE_GAP   = 3;
 
      const badge =
        isManualShape === true  ? ' ·U'  :
        isManualShape === false ? ' ·AI' : '';
      const displayName = nameText + badge;
 
      ctx.font = NAME_FONT;
      const nameW = nameText ? ctx.measureText(displayName).width : 0;
      ctx.font = VALUE_FONT;
      const valueW = valueText ? ctx.measureText(valueText).width : 0;
 
      const pillW  = Math.max(nameW, valueW) + PADDING_X * 2;
      const nameH  = nameText  ? 12 : 0;
      const valueH = valueText ? 14 : 0;
      const pillH  = nameH + (nameText && valueText ? LINE_GAP : 0) + valueH + PADDING_Y * 2;
 
      const { lx, ly } = findBestLabelPosition(anchorX, anchorY, pillW, pillH);
      placedLabels.push({ x: lx, y: ly, w: pillW, h: pillH });
 
      const pillCX = lx + pillW / 2;
      const pillCY = ly + pillH / 2;
 
      // Leader line
      ctx.beginPath();
      ctx.moveTo(anchorX, anchorY);
      ctx.lineTo(pillCX, pillCY);
      ctx.strokeStyle = `${color}77`;
      ctx.lineWidth = 0.8;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
 
      // Pill background
      const pillBg =
        isManualShape === true  ? 'rgba(37,  99, 235, 0.92)' :
        isManualShape === false ? 'rgba(99,  82, 210, 0.92)' :
                                  'rgba(18,  18,  38, 0.88)';
 
      ctx.fillStyle = pillBg;
      ctx.beginPath();
      (ctx as any).roundRect(lx, ly, pillW, pillH, 6);
      ctx.fill();
 
      ctx.strokeStyle = `${color}66`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
 
      if (nameText) {
        ctx.font = NAME_FONT;
        ctx.fillStyle = 'rgba(255,255,255,0.72)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(displayName, lx + PADDING_X, ly + PADDING_Y);
      }
 
      if (valueText) {
        ctx.font = VALUE_FONT;
        ctx.fillStyle = '#fbbf24';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const valueY = ly + PADDING_Y + (nameText ? nameH + LINE_GAP : 0);
        ctx.fillText(valueText, lx + PADDING_X, valueY);
      }
 
      ctx.restore();
    }
 
    // ── Shared: compute a shape's anchor point + measurement text ─────────
    //
    // Returns null if the shape type is unknown or has insufficient data.
 
    type AnchorInfo = {
      anchorX: number;
      anchorY: number;
      measurementText: string;
    };
 
    function getShapeAnchor(shape: Shape): AnchorInfo | null {
      switch (shape.type) {
        case 'line': {
          const line = shape as any;
          const sC = imageToCanvasCoordinates(line.startPoint, imageScale);
          const eC = imageToCanvasCoordinates(line.endPoint, imageScale);
          const unit =
            line.calculatedValueUnit ?? (pixelSpacing ? 'cm' : 'px');
          const measurementText =
            line.showOverlayValue && pixelSpacing
              ? formatMeasurement(
                  calculateLineLength(
                    line.startPoint,
                    line.endPoint,
                    pixelSpacing
                  ),
                  'cm'
                )
              : line.showOverlayValue && line.calculatedValue != null
                ? formatMeasurement(line.calculatedValue, unit)
                : formatMeasurement(
                    calculateLineLength(line.startPoint, line.endPoint, pixelSpacing),
                    pixelSpacing ? 'cm' : 'px'
                  );
          return {
            anchorX: (sC.x + eC.x) / 2,
            anchorY: (sC.y + eC.y) / 2,
            measurementText,
          };
        }
        case 'circle': {
          const circ = shape as any;
          const cC = imageToCanvasCoordinates(circ.center, imageScale);
          const rx = circ.radiusX * imageScale.scaleX * imageScale.zoomLevel;
          const ry = circ.radiusY * imageScale.scaleY * imageScale.zoomLevel;
          const unit =
            circ.calculatedValueUnit ?? (pixelSpacing ? 'cm' : 'px');
          let measurementText: string;
          if (circ.showOverlayValue && pixelSpacing) {
            measurementText = formatMeasurement(
              calculateEllipsePerimeter(
                circ.radiusX,
                circ.radiusY,
                pixelSpacing,
                pixel_spacing_unit
              ),
              'cm'
            );
          } else if (circ.showOverlayValue && circ.calculatedValue != null) {
            measurementText = formatMeasurement(circ.calculatedValue, unit);
          } else {
            const area = calculateEllipseArea(circ.radiusX, circ.radiusY, pixelSpacing);
            measurementText = `${formatMeasurement(
              calculateEllipsePerimeter(circ.radiusX, circ.radiusY, pixelSpacing, pixel_spacing_unit),
              pixelSpacing ? 'cm' : 'px'
            )} / ${formatMeasurement(area, pixelSpacing ? 'cm²' : 'px²')}`;
          }
          return {
            anchorX: cC.x,
            anchorY: cC.y - ry,
            measurementText,
          };
        }
        case 'square': {
          const sq = shape as any;
          if (!sq.startPoint || !sq.endPoint) return null;
          const sC = imageToCanvasCoordinates(sq.startPoint, imageScale);
          const eC = imageToCanvasCoordinates(sq.endPoint, imageScale);
          const x = Math.min(sC.x, eC.x);
          const y = Math.min(sC.y, eC.y);
          const w = Math.abs(eC.x - sC.x);
          return {
            anchorX: x + w / 2,
            anchorY: y, // top-centre of rect
            measurementText: formatMeasurement(
              calculateRectanglePerimeter(sq.startPoint, sq.endPoint, pixelSpacing),
              pixelSpacing ? 'cm' : 'px'
            ),
          };
        }
        case 'freehand': {
          const fh = shape as any;
          if (fh.points.length < 2) return null;
          const mid  = fh.points[Math.floor(fh.points.length / 2)];
          const midC = imageToCanvasCoordinates(mid, imageScale);
          return {
            anchorX: midC.x,
            anchorY: midC.y,
            measurementText: formatMeasurement(
              calculatePolylineLength(fh.points, pixelSpacing),
              pixelSpacing ? 'cm' : 'px'
            ),
          };
        }
        default:
          return null;
      }
    }
 
    // ─────────────────────────────────────────────────────────────────────
    // PASS 1 — Draw all shape outlines only (no labels, no dots)
    // ─────────────────────────────────────────────────────────────────────
 
    shapes.forEach((shape) => {
      const shapeCategoryId = (shape as any).categoryId;
      const isThisShapeHidden =
        shapeCategoryId != null &&
        (hiddenCategoryIds || []).some((id) => id === shapeCategoryId);
      if (isThisShapeHidden) return;
 
      ctx.save();
 
      const isHovered        = hoverState.hoveredShapeId === shape.id;
      const isMoveHovered    = isHovered && selectedTool === 'move';
      const isDrawingHovered = isHovered && ['line', 'circle', 'freehand', 'square'].includes(selectedTool);
      const shapeIsManual    = (shape as any).isManual === true;
 
      ctx.strokeStyle = shape.color;
      ctx.lineWidth   = shape.isSelected ? 3 : isMoveHovered ? 2.5 : shapeIsManual ? 2.5 : 2;
      ctx.setLineDash(shapeIsManual ? [] : [6, 4]);
 
      if (isMoveHovered && !shape.isSelected) {
        ctx.shadowColor = shape.color;
        ctx.shadowBlur  = 5;
      } else if (isDrawingHovered && !shape.isSelected) {
        ctx.shadowColor = '#ffa726';
        ctx.shadowBlur  = 3;
        ctx.globalAlpha = 0.8;
      }
 
      switch (shape.type) {
        case 'line': {
          const line = shape as any;
          const sC = imageToCanvasCoordinates(line.startPoint, imageScale);
          const eC = imageToCanvasCoordinates(line.endPoint, imageScale);
          ctx.beginPath(); ctx.moveTo(sC.x, sC.y); ctx.lineTo(eC.x, eC.y); ctx.stroke();
          break;
        }
        case 'circle': {
          const circ = shape as any;
          const cC = imageToCanvasCoordinates(circ.center, imageScale);
          const rx  = circ.radiusX * imageScale.scaleX * imageScale.zoomLevel;
          const ry  = circ.radiusY * imageScale.scaleY * imageScale.zoomLevel;
          const rot =
            typeof circ.rotation === 'number' && Number.isFinite(circ.rotation)
              ? circ.rotation : 0;
          ctx.beginPath();
          ctx.ellipse(cC.x, cC.y, rx, ry, rot, 0, 2 * Math.PI);
          ctx.stroke();
          break;
        }
        case 'square': {
          const sq = shape as any;
          if (sq.startPoint && sq.endPoint) {
            const sC = imageToCanvasCoordinates(sq.startPoint, imageScale);
            const eC = imageToCanvasCoordinates(sq.endPoint, imageScale);
            ctx.beginPath();
            ctx.rect(
              Math.min(sC.x, eC.x), Math.min(sC.y, eC.y),
              Math.abs(eC.x - sC.x), Math.abs(eC.y - sC.y)
            );
            ctx.stroke();
          }
          break;
        }
        case 'freehand': {
          const fh = shape as any;
          if (fh.points.length < 2) break;
          ctx.lineJoin = 'round';
          ctx.lineCap  = 'round';
          ctx.beginPath();
          buildSmoothFreehandPath(ctx, fh.points, imageScale);
          ctx.stroke();
          break;
        }
      }
 
      ctx.restore();
    });
 
    // ─────────────────────────────────────────────────────────────────────
    // PASS 2 — Draw all dots + labels on top of every shape outline.
    //
    // Render order within Pass 2:
    //   a) inactive shapes first (dots only, no pill)
    //   b) active shape (hovered/selected) last so its pill is on the very top
    // ─────────────────────────────────────────────────────────────────────
 
    if (!showLabels) return;
 
    // Split into active vs inactive so active renders last (highest z)
    const inactiveShapes = shapes.filter(
      (s) => s.id !== hoverState.hoveredShapeId && !s.isSelected
    );
    const activeShapes = shapes.filter(
      (s) => s.id === hoverState.hoveredShapeId || s.isSelected
    );
 
    const renderLabelLayer = (shape: Shape) => {
      const shapeCategoryId = (shape as any).categoryId;
      const isThisShapeHidden =
        shapeCategoryId != null &&
        (hiddenCategoryIds || []).some((id) => id === shapeCategoryId);
      if (isThisShapeHidden || !shape.label) return;
 
      const anchor = getShapeAnchor(shape);
      if (!anchor) return;
 
      const { anchorX, anchorY, measurementText } = anchor;
      const isActive = shape.id === hoverState.hoveredShapeId || !!shape.isSelected;
 
      // Always-visible dot
      drawAnchorDot(anchorX, anchorY, shape.color, isActive);
 
      // Full pill: active shapes, or always on touch
      if (isActive || isTouchDevice) {
        const rawLabel     = String(shape.label ?? '');
        const cleanedLabel = rawLabel.replace('Not Visible', '').trim();
        const rawFlagCode  = String((shape as any).backendLabel ?? (shape as any).flagCode ?? '');
        const normalizedFlag = rawFlagCode
          .replace(/^QUAL_FLAG__/, '')
          .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '_');
        const rawBackendLabel = String((shape as any).backendLabel ?? '');
        const measKey = rawBackendLabel.startsWith('MEAS__')
          ? rawBackendLabel.split(/\s+/)[0]
          : '';
        const displayName = measKey
          ? getMeasurementDisplayName(measKey)
          : normalizedFlag
            ? t(`qualityFlag.${normalizedFlag}`, { defaultValue: cleanedLabel })
            : cleanedLabel;
 
        const isManualVal: boolean | null =
          (shape as any).isManual === true  ? true  :
          (shape as any).isManual === false ? false : null;
 
        drawSmartLabel(anchorX, anchorY, displayName, measurementText, isManualVal, shape.color);
      }
    };
 
    // inactive first (dots only, behind active pills)
    inactiveShapes.forEach(renderLabelLayer);
    // active last (pill sits above everything)
    activeShapes.forEach(renderLabelLayer);
  },
  [
    shapes,
    imageScale,
    pixelSpacing,
    pixel_spacing_unit,
    hoverState,
    selectedTool,
    annotationsVisible,
    showLabels,
    hiddenCategoryIds,
    t,
  ]
);
  // Draw edit handles for selected shapes and hovered shapes (when using move tool)
  const drawEditHandles = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!annotationsVisible) return; // Skip drawing handles if annotations are hidden

      // Detect if device supports touch (for showing larger handles)
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

      shapes.forEach((shape) => {
        const shouldShowHandles =
          shape.isSelected ||
          (selectedTool === 'move' && hoverState.hoveredShapeId === shape.id) ||
          (selectedTool === 'move' &&
            touchEditState.potentialHandle?.shapeId === shape.id);

        if (!shouldShowHandles) return;

        const controlPoints = getShapeControlPoints(shape);
        // Larger handles for touch/tablet (matches TOUCH_HANDLE_HIT_RADIUS sensitivity)
        const handleSize = isTouchDevice ? 14 : 8;

        ctx.save();

        Object.entries(controlPoints).forEach(([handleName, point]) => {
          const canvasPoint = imageToCanvasCoordinates(point, imageScale);

          // Skip center handle for circles (we only want perimeter handles)
          if (shape.type === 'circle' && handleName === 'center') return;

          // Check if this handle is being hovered or is the potential touch target
          const isHovered =
            hoverState.hoveredHandle === handleName &&
            hoverState.hoveredShapeId === shape.id;
          const isTouchTarget =
            touchEditState.potentialHandle?.shapeId === shape.id &&
            touchEditState.potentialHandle?.handleName === handleName;
          const isBeingEdited =
            editState.editingShapeId === shape.id &&
            editState.editingHandle === handleName;

          // Draw handle background (white circle with border)
          if (isBeingEdited) {
            // Active editing state - bright green
            ctx.fillStyle = '#bbf7d0';
            ctx.strokeStyle = '#16a34a';
            ctx.lineWidth = 3;
          } else if (isTouchTarget) {
            // Touch target (long-press pending) - pulsing blue
            ctx.fillStyle = '#dbeafe';
            ctx.strokeStyle = '#2563eb';
            ctx.lineWidth = 3;
          } else if (isHovered) {
            ctx.fillStyle = '#e6f3ff';
            ctx.strokeStyle = '#0066aa';
            ctx.lineWidth = 3;
          } else {
            ctx.fillStyle = 'white';
            ctx.strokeStyle = '#007acc';
            ctx.lineWidth = 2;
          }

          // Rotation handle: draw larger on touch so it’s easier to see and tap (not confused with ellipse edge)
          const isRotationHandle = shape.type === 'circle' && handleName === 'rotation';
          const baseSize = isRotationHandle && isTouchDevice ? 11 : handleSize / 2;
          const currentHandleSize =
            isHovered || isTouchTarget || isBeingEdited
              ? baseSize + 2
              : baseSize;

          ctx.beginPath();
          ctx.arc(
            canvasPoint.x,
            canvasPoint.y,
            currentHandleSize,
            0,
            2 * Math.PI
          );
          ctx.fill();
          ctx.stroke();

          // Add a small dot in the center for better visibility
          ctx.fillStyle = isBeingEdited
            ? '#16a34a'
            : isTouchTarget
              ? '#2563eb'
              : isHovered
                ? '#0066aa'
                : '#007acc';
          ctx.beginPath();
          ctx.arc(
            canvasPoint.x,
            canvasPoint.y,
            isTouchDevice ? 3 : 2,
            0,
            2 * Math.PI
          );
          ctx.fill();
        });

        ctx.restore();
      });
    },
    [
      shapes,
      imageScale,
      hoverState,
      selectedTool,
      annotationsVisible,
      touchEditState,
      editState,
    ]
  );

  // Draw the base image and shapes (when videoMode, only clear and draw shapes — video is behind the canvas).
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (videoMode) {
      drawShapes(ctx);
      drawEditHandles(ctx);
      return;
    }

    if (!loadedImage) return;
    const effectiveScaleX = imageScale.scaleX * imageScale.zoomLevel;
    const effectiveScaleY = imageScale.scaleY * imageScale.zoomLevel;
    const scaledWidth = loadedImage.width * effectiveScaleX;
    const scaledHeight = loadedImage.height * effectiveScaleY;
    const imageX = imageScale.offsetX + imageScale.panX;
    const imageY = imageScale.offsetY + imageScale.panY;
    ctx.drawImage(loadedImage, imageX, imageY, scaledWidth, scaledHeight);
    drawShapes(ctx);
    drawEditHandles(ctx);
  }, [loadedImage, imageScale, drawShapes, drawEditHandles, videoMode]);

  // Draw preview shape during drawing
  const drawPreview = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      startPoint: Point,
      currentPoint: Point,
      tool: DrawingTool
    ) => {
      ctx.save();
      ctx.strokeStyle = '#00ff88'; // Bright light green for better visibility on ultrasound images
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.setLineDash([5, 5]);

      const startCanvas = imageToCanvasCoordinates(startPoint, imageScale);
      const currentCanvas = imageToCanvasCoordinates(currentPoint, imageScale);

      switch (tool) {
        case 'line':
          ctx.beginPath();
          ctx.moveTo(startCanvas.x, startCanvas.y);
          ctx.lineTo(currentCanvas.x, currentCanvas.y);
          ctx.stroke();
          break;

        case 'circle':
          // Ellipse: drag bounding box (same as rectangle), ellipse inscribed
          const cxC = (startCanvas.x + currentCanvas.x) / 2;
          const cyC = (startCanvas.y + currentCanvas.y) / 2;
          const rXc = Math.max(Math.abs(currentCanvas.x - startCanvas.x) / 2, 1);
          const rYc = Math.max(Math.abs(currentCanvas.y - startCanvas.y) / 2, 1);
          ctx.beginPath();
          ctx.ellipse(cxC, cyC, rXc, rYc, 0, 0, 2 * Math.PI);
          ctx.stroke();
          break;

        case 'square':
          ctx.beginPath();
          ctx.rect(
            Math.min(startCanvas.x, currentCanvas.x),
            Math.min(startCanvas.y, currentCanvas.y),
            Math.abs(currentCanvas.x - startCanvas.x),
            Math.abs(currentCanvas.y - startCanvas.y)
          );
          ctx.stroke();
          break;

        case 'freehand':
          // For freehand, draw the current path plus a preview line back to the first point
          if (
            drawingState.currentShape &&
            drawingState.currentShape.type === 'freehand'
          ) {
            const freehandShape = drawingState.currentShape as any;

            if (freehandShape.points.length >= 1) {
              ctx.lineJoin = 'round';
              ctx.lineCap = 'round';
              ctx.beginPath();
              const cvPts = freehandShape.points.map((p: Point) =>
                imageToCanvasCoordinates(p, imageScale)
              );
              cvPts.push(currentCanvas);
              if (freehandShape.points.length >= 3) {
                buildSmoothFreehandCanvasPolyline(ctx, cvPts, true);
              } else {
                buildSmoothFreehandCanvasPolyline(ctx, cvPts, false);
              }
              ctx.stroke();
            }
          }
          break;
      }

      ctx.restore();
    },
    [imageScale, drawingState]
  );

  // Get mouse position relative to canvas
  const getMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): Point => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      // Account for canvas scaling - convert from display coordinates to canvas coordinates
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  // Find shape at point (for selection) - topmost only
  const findShapeAtPoint = useCallback(
    (imagePoint: Point): Shape | null => {
      // Search from top to bottom (last drawn first)
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        if (isPointInShape(imagePoint, shape)) {
          return shape;
        }
      }
      return null;
    },
    [shapes]
  );

  // Approximate area of a shape – used to sort overlapping shapes smallest-first
  // so clicking inside HC where thalamus sits picks thalamus in one click (not two)
  const getShapeApproxArea = useCallback((shape: Shape): number => {
    switch (shape.type) {
      case 'line': {
        const s = shape as any;
        const dx = s.endPoint.x - s.startPoint.x;
        const dy = s.endPoint.y - s.startPoint.y;
        // Lines have near-zero area; weight by length so shorter lines sort before longer ones
        return Math.sqrt(dx * dx + dy * dy) * 10;
      }
      case 'circle': {
        const s = shape as any;
        return Math.PI * (s.radiusX ?? 1) * (s.radiusY ?? 1);
      }
      case 'square': {
        const s = shape as any;
        return (
          Math.abs(s.endPoint.x - s.startPoint.x) *
          Math.abs(s.endPoint.y - s.startPoint.y)
        );
      }
      case 'freehand': {
        const s = shape as any;
        if (!s.points || s.points.length < 2) return 0;
        const xs: number[] = s.points.map((p: any) => p.x);
        const ys: number[] = s.points.map((p: any) => p.y);
        return (
          (Math.max(...xs) - Math.min(...xs)) *
          (Math.max(...ys) - Math.min(...ys))
        );
      }
      default:
        return Infinity;
    }
  }, []);

  // Find all shapes at point, sorted smallest-first so the innermost/most-specific shape is preferred
  const findAllShapesAtPoint = useCallback(
    (imagePoint: Point): Shape[] => {
      const result: Shape[] = [];
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        if (isPointInShape(imagePoint, shape)) {
          result.push(shape);
        }
      }
      // Sort smallest area first: a click on thalamus (small) inside HC (large ellipse)
      // will directly pick thalamus without needing a second click
      result.sort((a, b) => getShapeApproxArea(a) - getShapeApproxArea(b));
      return result;
    },
    [shapes, getShapeApproxArea]
  );

  // Find any control handle at canvas point (for touch editing - searches all shapes)
  const findAnyControlHandleAt = useCallback(
    (
      canvasPoint: Point,
      hitRadius: number = 20
    ): { shapeId: string; handleName: string } | null => {
      // Search from top to bottom (last drawn first)
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        const handleName = findControlHandleAt(
          canvasPoint,
          shape,
          imageScale,
          hitRadius
        );
        if (handleName) {
          return { shapeId: shape.id, handleName };
        }
      }
      return null;
    },
    [shapes, imageScale]
  );

  // Hide context menu on regular clicks
  const hideContextMenu = useCallback(() => {
    if (contextMenu.visible) {
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        shapeId: null,
        showLabelMenu: false,
      });
      // Also deselect any selected shapes when closing context menu
      if (onShapeSelect) {
        onShapeSelect(null);
      }
    }
  }, [contextMenu.visible, onShapeSelect]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Only handle left mouse button (button 0), ignore right-click (button 2)
      if (e.button !== 0) return;

      // Hide context menu on any click
      hideContextMenu();

      // Clear ruler measurement when starting any other action
      if (
        selectedTool !== 'ruler' &&
        (rulerState.startPoint || rulerState.endPoint)
      ) {
        setRulerState({
          isMeasuring: false,
        });
      }

      const mousePos = getMousePos(e);
      const imagePos = canvasToImageCoordinates(mousePos, imageScale);

      if (selectedTool === 'delete' && annotationsVisible && onShapeDelete) {
        const allAtPoint = findAllShapesAtPoint(imagePos);
        if (allAtPoint.length > 0) {
          const samePoint =
            lastDeletePointRef.current &&
            Math.hypot(
              imagePos.x - lastDeletePointRef.current.x,
              imagePos.y - lastDeletePointRef.current.y
            ) <= DELETE_CYCLE_TOLERANCE;
          if (allAtPoint.length === 1 || !samePoint) {
            lastDeletePointRef.current = { ...imagePos };
            deleteCycleIndexRef.current = 0;
          } else {
            deleteCycleIndexRef.current =
              (deleteCycleIndexRef.current + 1) % allAtPoint.length;
          }
          const cycleIndex = Math.min(
            deleteCycleIndexRef.current,
            allAtPoint.length - 1
          );
          const toDelete = allAtPoint[cycleIndex];
          onShapeDelete(toDelete.id);
        }
        return;
      }

      if (selectedTool === 'pan' && annotationsVisible) {
        // Pan (hand): move structure only – no resize handles.
        // Selection-state cycling: if topmost shape is already selected, pick the next one so inner structures are reachable.
        if (!isEditMode) return; 
        const allAtPoint = findAllShapesAtPoint(imagePos);
        let hitShape: Shape | null = null;
        if (allAtPoint.length > 0) {
          const selectedId = shapes.find(s => s.isSelected)?.id ?? null;
          const currentIdx = selectedId ? allAtPoint.findIndex(s => s.id === selectedId) : -1;
          if (currentIdx >= 0) {
            // Currently selected shape is in the pile – cycle to next
            hitShape = allAtPoint[(currentIdx + 1) % allAtPoint.length];
          } else {
            hitShape = allAtPoint[0];
          }
        }
        if (hitShape) {
          if (onShapeSelect) onShapeSelect(hitShape.id);
          if (onDragStart) onDragStart();
          setDragState({
            isDragging: true,
            draggedShapeId: hitShape.id,
            lastMousePos: imagePos,
          });
        } else {
          if (onShapeSelect) onShapeSelect(null);
        }
        return;
      }
      if (selectedTool === 'move' && annotationsVisible) {
        // Edit: first check control handles (resize); then shape body with same-spot cycle for inner structure
        if (!isEditMode) return; 
        let handleHit = false;
        for (const shape of shapes) {
          // Check handles on selected shapes or hovered shapes
          const shouldCheckHandles =
            shape.isSelected || hoverState.hoveredShapeId === shape.id;

          if (shouldCheckHandles) {
            const handleName = findControlHandleAt(
              mousePos,
              shape,
              imageScale,
              12
            );

            if (handleName) {
              // Select the shape first if not already selected
              if (!shape.isSelected && onShapeSelect) {
                onShapeSelect(shape.id);
              }

              // Start editing the handle
              if (onDragStart) {
                onDragStart();
              }
              setEditState({
                isEditing: true,
                editingShapeId: shape.id,
                editingHandle: handleName,
              });
              handleHit = true;
              break;
            }
          }
        }

        // No handle hit – select shape body.
        // Selection-state cycling: if topmost shape is already selected, cycle to next so inner shapes (e.g. thalamus under HC) are reachable.
        if (!handleHit) {
          const allAtPoint = findAllShapesAtPoint(imagePos);
          let hitShape: Shape | null = null;
          if (allAtPoint.length > 0) {
            const selectedId = shapes.find(s => s.isSelected)?.id ?? null;
            const currentIdx = selectedId ? allAtPoint.findIndex(s => s.id === selectedId) : -1;
            if (currentIdx >= 0) {
              hitShape = allAtPoint[(currentIdx + 1) % allAtPoint.length];
            } else {
              hitShape = allAtPoint[0];
            }
          }

          if (hitShape) {
            if (onShapeSelect) onShapeSelect(hitShape.id);
            if (onDragStart) onDragStart();
            setDragState({
              isDragging: true,
              draggedShapeId: hitShape.id,
              lastMousePos: imagePos,
            });
          } else {
            if (onShapeSelect) onShapeSelect(null);
          }
        }
      } else if (!contextMenu.visible) {
  const imagePos = canvasToImageCoordinates(mousePos, imageScale);
  
  // FIX: Check tool type first. If we are in a drawing tool mode, 
  // we should ignore existing shapes and start drawing.
  const isDrawingTool = ['line', 'circle', 'freehand', 'square'].includes(selectedTool);

  if (isDrawingTool && annotationsVisible && isEditMode) {
    if (selectedTool === 'freehand') {
      setDrawingState({
        isDrawing: true,
        startPoint: imagePos,
        currentShape: { type: 'freehand', points: [imagePos] } as Omit<FreehandShape, 'id'>,
      });
    } else {
      // Line, ellipse (circle), square: single drag from start to end
      setDrawingState({ isDrawing: true, startPoint: imagePos });
    }
  } else if (selectedTool === 'ruler') {
    setRulerState({ isMeasuring: true, startPoint: imagePos, endPoint: imagePos });
  }
}
    },
    [
      selectedTool,
      getMousePos,
      imageScale,
      findShapeAtPoint,
      findAllShapesAtPoint,
      onShapeSelect,
      onShapeDelete,
      onDragStart,
      shapes,
      hoverState,
      annotationsVisible,
      isEditMode,
      hideContextMenu,
      contextMenu.visible,
      drawingState,
      rulerState,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const mousePos = getMousePos(e);
      const imagePos = canvasToImageCoordinates(mousePos, imageScale);
      setCurrentMousePos(imagePos);

      // Update hover state (annotations visible, not dragging/editing; both move and pan do shape hover, move also does handle hover)
      if (
        annotationsVisible &&
        !dragState.isDragging &&
        !editState.isEditing
      ) {
        let newHoveredShapeId: string | null = null;
        let newHoveredHandle: string | null = null;

        if (selectedTool === 'move') {
          // For move tool, check for handle hover first
          for (const shape of shapes) {
            if (shape.isSelected || hoverState.hoveredShapeId === shape.id) {
              const handleName = findControlHandleAt(
                mousePos,
                shape,
                imageScale,
                12
              );
              if (handleName) {
                newHoveredHandle = handleName;
                newHoveredShapeId = shape.id;
                break;
              }
            }
          }

          // If no handle hovered, check for shape hover (overlapping: prefer selected so inner structure is target)
          if (!newHoveredHandle) {
            const allAtPoint = findAllShapesAtPoint(imagePos);
            const selectedId = shapes.find((s) => s.isSelected)?.id ?? null;
            const hitShape =
              allAtPoint.length > 0
                ? (selectedId && allAtPoint.some((s) => s.id === selectedId)
                    ? allAtPoint.find((s) => s.id === selectedId)!
                    : allAtPoint[0])
                : null;
            setHoverState({ hoveredShapeId: hitShape?.id || null, hoveredHandle: null });
            if (hitShape) {
              newHoveredShapeId = hitShape.id;
            }
          }
        } else if (selectedTool === 'pan') {
          // Pan (hand): shape hover only, no handles; same overlapping logic so inner structure (e.g. thalamus) is target
          const allAtPoint = findAllShapesAtPoint(imagePos);
          const selectedId = shapes.find((s) => s.isSelected)?.id ?? null;
          const hitShape =
            allAtPoint.length > 0
              ? (selectedId && allAtPoint.some((s) => s.id === selectedId)
                  ? allAtPoint.find((s) => s.id === selectedId)!
                  : allAtPoint[0])
              : null;
          setHoverState({ hoveredShapeId: hitShape?.id || null, hoveredHandle: null });
          if (hitShape) {
            newHoveredShapeId = hitShape.id;
          }
        } else if (
          ['line', 'circle', 'freehand', 'square'].includes(selectedTool)
        ) {
          // For drawing tools, only check for shape hover (to show pointer cursor)
          const hitShape = findShapeAtPoint(imagePos);
          if (hitShape) {
            newHoveredShapeId = hitShape.id;
          }
        }

        // Update hover state if it changed
        if (
          newHoveredShapeId !== hoverState.hoveredShapeId ||
          newHoveredHandle !== hoverState.hoveredHandle
        ) {
          setHoverState({
            hoveredShapeId: newHoveredShapeId,
            hoveredHandle: newHoveredHandle,
          });
        }
      } else if (hoverState.hoveredShapeId || hoverState.hoveredHandle) {
        // Clear hover state when not using appropriate tools or when actively doing something
        setHoverState({
          hoveredShapeId: null,
          hoveredHandle: null,
        });
      }

      // Handle shape handle editing
      if (
        editState.isEditing &&
        editState.editingShapeId &&
        editState.editingHandle &&
        onShapeUpdate
      ) {
        // Find the shape being edited
        const editingShape = shapes.find(
          (s) => s.id === editState.editingShapeId
        );
        if (editingShape) {
          // Update the shape based on handle movement
          const updatedShape = updateShapeHandle(
            editingShape,
            editState.editingHandle,
            imagePos
          );
          onShapeUpdate(editState.editingShapeId, updatedShape);
        }
        return;
      }

      // Handle shape dragging
      if (
        dragState.isDragging &&
        dragState.draggedShapeId &&
        dragState.lastMousePos &&
        onShapeUpdate
      ) {
        const deltaX = imagePos.x - dragState.lastMousePos.x;
        const deltaY = imagePos.y - dragState.lastMousePos.y;

        // Find the shape being dragged
        const draggedShape = shapes.find(
          (s) => s.id === dragState.draggedShapeId
        );
        if (draggedShape) {
          // Move the shape
          const movedShape = moveShape(draggedShape, deltaX, deltaY);
          onShapeUpdate(dragState.draggedShapeId, movedShape);
        }

        // Update last mouse position
        setDragState((prev) => ({
          ...prev,
          lastMousePos: imagePos,
        }));
        return;
      }

      // Handle ruler measurement update
      if (rulerState.isMeasuring && rulerState.startPoint) {
        setRulerState((prev) => ({
          ...prev,
          endPoint: imagePos,
        }));
      }

      // Handle freehand drawing (capture mouse movement)
      if (
        selectedTool === 'freehand' &&
        drawingState.isDrawing &&
        drawingState.currentShape &&
        drawingState.currentShape.type === 'freehand'
      ) {
        const freehandShape = drawingState.currentShape as any;
        const lastPoint = freehandShape.points[freehandShape.points.length - 1];

        // Only add point if it's far enough from the last point (smooth the path)
        const distance = Math.sqrt(
          Math.pow(imagePos.x - lastPoint.x, 2) +
            Math.pow(imagePos.y - lastPoint.y, 2)
        );

        if (distance > 1.5) {
          // Minimum spacing in image pixels — slightly denser than before for smoother strokes
          const newPoints = [...freehandShape.points, imagePos];
          setDrawingState({
            ...drawingState,
            currentShape: {
              ...freehandShape,
              points: newPoints,
            },
          });
        }
      }

      // Handle drawing preview
      if (drawingState.isDrawing && drawingState.startPoint) {
        // Redraw canvas with preview
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Redraw everything
        redrawCanvas();

        // Draw preview of current shape
        drawPreview(ctx, drawingState.startPoint, imagePos, selectedTool);
      }
    },
    [
      drawingState,
      dragState,
      panState,
      editState,
      hoverState,
      selectedTool,
      getMousePos,
      imageScale,
      redrawCanvas,
      drawPreview,
      shapes,
      onShapeUpdate,
      findShapeAtPoint,
      findAllShapesAtPoint,
      annotationsVisible,
      rulerState,
    ]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Handle pan end (clear ref so pan only ever moved the view, never shapes)
      if (panState.isPanning || panningRef.current) {
        panningRef.current = null;
        setPanState({
          isPanning: false,
          lastPanPos: null,
        });
        return;
      }

      // Handle edit end
      if (editState.isEditing) {
        // Call drag end handler for history
        if (onDragEnd) {
          onDragEnd();
        }
        setEditState({
          isEditing: false,
          editingShapeId: null,
          editingHandle: null,
        });
        return;
      }

      // Handle drag end – keep selection so next click on overlap can cycle to inner shape (e.g. thalamus under HC)
      if (dragState.isDragging) {
        if (onDragEnd) {
          onDragEnd();
        }
        setDragState({
          isDragging: false,
          draggedShapeId: null,
          lastMousePos: null,
        });
        return;
      }

      // Handle ruler measurement end (keep it visible until next action)
      if (rulerState.isMeasuring) {
        setRulerState((prev) => ({
          ...prev,
          isMeasuring: false,
        }));
        return;
      }

      // Handle drawing completion
      if (drawingState.isDrawing && drawingState.startPoint) {
        const mousePos = getMousePos(e);
        const imagePos = canvasToImageCoordinates(mousePos, imageScale);

        // Create the final shape
        let newShape: Omit<Shape, 'id'> | null = null;

        switch (selectedTool) {
          case 'line':
            newShape = {
              type: 'line' as const,
              startPoint: drawingState.startPoint,
              endPoint: imagePos,
            } as Omit<Shape, 'id'>;
            break;

          case 'circle': {
            // Ellipse from drag bounding box: center = midpoint, radiusX/radiusY = half width/height
            const sx = drawingState.startPoint.x;
            const sy = drawingState.startPoint.y;
            const ex = imagePos.x;
            const ey = imagePos.y;
            const cx = (sx + ex) / 2;
            const cy = (sy + ey) / 2;
            const radiusX = Math.max(Math.abs(ex - sx) / 2, 1);
            const radiusY = Math.max(Math.abs(ey - sy) / 2, 1);
            newShape = {
              type: 'circle' as const,
              center: { x: cx, y: cy },
              radiusX,
              radiusY,
              rotation: 0,
            } as Omit<Shape, 'id'>;
            break;
          }

          case 'square':
            newShape = {
              type: 'square' as const,
              startPoint: drawingState.startPoint,
              endPoint: imagePos,
            } as Omit<Shape, 'id'>;
            break;

          case 'freehand':
            if (
              drawingState.currentShape &&
              drawingState.currentShape.type === 'freehand'
            ) {
              const freehandShape = drawingState.currentShape as any;
              if (freehandShape.points.length >= 3) {
                // Close the shape by adding the first point at the end (if not already closed)
                const isAlreadyClosed = isFreehandShapeClosed(
                  freehandShape.points
                );
                const finalPoints = isAlreadyClosed
                  ? freehandShape.points
                  : [...freehandShape.points, freehandShape.points[0]];

                newShape = {
                  type: 'freehand' as const,
                  points: simplifyFreehandPoints(finalPoints),
                } as Omit<Shape, 'id'>;
              } else if (freehandShape.points.length >= 2) {
                newShape = {
                  type: 'freehand' as const,
                  points: simplifyFreehandPoints(freehandShape.points),
                } as Omit<Shape, 'id'>;
              }
            }
            break;
        }

        if (newShape && onShapeAdd) {
          // Apply preset label if active
          if (presetLabel) {
            onShapeAdd({ ...newShape, label: presetLabel, color: presetColor });
          } else {
            onShapeAdd(newShape);
          }
        }

        setDrawingState({ isDrawing: false });
      }
    },
    [
      drawingState,
      dragState,
      panState,
      editState,
      selectedTool,
      getMousePos,
      imageScale,
      onShapeAdd,
      onShapeSelect,
      onDragEnd,
      rulerState,
      presetLabel,
      presetColor,
    ]
  );

  // Handle double-click (currently not used, but keeping for potential future features)
  const handleDoubleClick = useCallback(() => {
    // Future: Could be used for other double-click interactions
  }, []);

  // Handle right-click context menu
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault(); // Prevent default browser context menu

      if (!annotationsVisible) return; // No context menu if annotations are disabled

      const mousePos = getMousePos(e);
      const imagePos = canvasToImageCoordinates(mousePos, imageScale);

      // Find shape at right-click position; if multiple overlap (e.g. Heart under Abdo), use selected if it's under cursor
      const allAtPoint = findAllShapesAtPoint(imagePos);
      const selectedShapeId = shapes.find((s) => s.isSelected)?.id ?? null;
      const hitShape =
        allAtPoint.length > 0
          ? (selectedShapeId && allAtPoint.some((s) => s.id === selectedShapeId)
              ? allAtPoint.find((s) => s.id === selectedShapeId)!
              : allAtPoint[0])
          : null;

      if (hitShape) {
        // Check if this is a segmentation from backend (has dbId, backendLabel, or backendType)
        const isBackendSegmentation =
          hitShape.dbId !== undefined ||
          hitShape.backendLabel !== undefined ||
          hitShape.backendType !== undefined;

        // Disable context menu for backend segmentations only
        if (isBackendSegmentation) {
          console.log(
            'Right-click on backend segmentation - menu disabled',
            hitShape
          );
          // Just prevent the menu, but don't show anything
          return;
        }

        // Show context menu for user-drawn shapes (not from backend)
        console.log('Right-click on user-drawn shape - showing menu', hitShape);
        setContextMenu({
          visible: true,
          x: e.clientX, // Use screen coordinates for positioning
          y: e.clientY,
          shapeId: hitShape.id,
          showLabelMenu: false,
        });

        // Select the shape if not already selected
        if (!hitShape.isSelected && onShapeSelect) {
          onShapeSelect(hitShape.id);
        }
      } else {
        // Hide context menu if right-clicking on empty space
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          shapeId: null,
          showLabelMenu: false,
        });
      }
    },
    [
      annotationsVisible,
      getMousePos,
      imageScale,
      findShapeAtPoint,
      findAllShapesAtPoint,
      shapes,
      onShapeSelect,
    ]
  );

  // Handle context menu delete action
  const handleContextMenuDelete = useCallback(() => {
    if (contextMenu.shapeId && onShapeDelete) {
      onShapeDelete(contextMenu.shapeId);
    }
    // Hide context menu after action
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      shapeId: null,
      showLabelMenu: false,
    });
  }, [contextMenu.shapeId, onShapeDelete]);

  // Handle label selection
  const handleLabelSelect = useCallback(
    (label: string) => {
      if (contextMenu.shapeId && onShapeUpdate) {
        onShapeUpdate(contextMenu.shapeId, { label });
      }
      // Hide context menu after action
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        shapeId: null,
        showLabelMenu: false,
      });
    },
    [contextMenu.shapeId, onShapeUpdate]
  );

  // Toggle label submenu
  const handleToggleLabelMenu = useCallback(() => {
    setContextMenu((prev) => ({
      ...prev,
      showLabelMenu: !prev.showLabelMenu,
    }));
  }, []);

  // Touch event handlers - convert touch to mouse-like events
  const getTouchPos = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>): Point => {
      const canvas = canvasRef.current;
      if (!canvas || e.touches.length === 0) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];

      // Account for canvas scaling - convert from display coordinates to canvas coordinates
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  // Tablet/touch sensitivity constants (tune for better feel on tablet)
  const LONG_PRESS_DURATION = 220; // ms – shorter = more responsive move/edit
  const TOUCH_MOVE_THRESHOLD = 18; // px – movement under this doesn't cancel long-press (forgiveness for wobble)
  const TOUCH_HANDLE_HIT_RADIUS = 28; // px – larger = easier to grab resize handles on touch
  const TOUCH_PAN_SENSITIVITY = 1.15; // multiplier for view pan (1.0 = normal; >1 = more sensitive)

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      // Prevent default to avoid scrolling and double-tap zoom
      e.preventDefault();

      // Only handle single touch
      if (e.touches.length !== 1) return;

      // Convert to mouse event format
      const touchPos = getTouchPos(e);
      const imagePos = canvasToImageCoordinates(touchPos, imageScale);

      // Hide context menu on any touch
      hideContextMenu();

      // Clear any existing long-press timer
      if (touchEditState.longPressTimer) {
        clearTimeout(touchEditState.longPressTimer);
      }

      // Clear ruler measurement when starting any other action
      if (
        selectedTool !== 'ruler' &&
        (rulerState.startPoint || rulerState.endPoint)
      ) {
        setRulerState({
          isMeasuring: false,
        });
      }

      // Delete tool on touch: same logic as mouse (tap = delete shape at point, cycle if overlapping)
      if (selectedTool === 'delete' && annotationsVisible && onShapeDelete) {
        const allAtPoint = findAllShapesAtPoint(imagePos);
        if (allAtPoint.length > 0) {
          const samePoint =
            lastDeletePointRef.current &&
            Math.hypot(
              imagePos.x - lastDeletePointRef.current.x,
              imagePos.y - lastDeletePointRef.current.y
            ) <= DELETE_CYCLE_TOLERANCE;
          if (allAtPoint.length === 1 || !samePoint) {
            lastDeletePointRef.current = { ...imagePos };
            deleteCycleIndexRef.current = 0;
          } else {
            deleteCycleIndexRef.current =
              (deleteCycleIndexRef.current + 1) % allAtPoint.length;
          }
          const cycleIndex = Math.min(
            deleteCycleIndexRef.current,
            allAtPoint.length - 1
          );
          const toDelete = allAtPoint[cycleIndex];
          onShapeDelete(toDelete.id);
        }
        return;
      }

      if (selectedTool === 'pan' && annotationsVisible) {
        // Pan (hand): move structure only. Selection-state cycling for inner structures.
        const allAtPoint = findAllShapesAtPoint(imagePos);
        let hitShape: Shape | null = null;
        if (allAtPoint.length > 0) {
          const selectedId = shapes.find(s => s.isSelected)?.id ?? null;
          const currentIdx = selectedId ? allAtPoint.findIndex(s => s.id === selectedId) : -1;
          hitShape = currentIdx >= 0
            ? allAtPoint[(currentIdx + 1) % allAtPoint.length]
            : allAtPoint[0];
        }
        if (hitShape) {
          const timer = setTimeout(() => {
            if (onDragStart) onDragStart();
            setDragState({
              isDragging: true,
              draggedShapeId: hitShape!.id,
              lastMousePos: imagePos,
            });
            setTouchEditState((prev) => ({ ...prev, isLongPressActive: true, longPressTimer: null }));
          }, LONG_PRESS_DURATION);
          setTouchEditState({
            longPressTimer: timer,
            touchStartPos: touchPos,
            potentialHandle: null,
            isLongPressActive: false,
          });
          if (onShapeSelect) onShapeSelect(hitShape.id);
        } else {
          // No shape under touch – start view pan (move the image) immediately for tablet
          if (onShapeSelect) onShapeSelect(null);
          panningRef.current = { lastPanPos: touchPos };
          setPanState({ isPanning: true, lastPanPos: touchPos });
          setTouchEditState({
            longPressTimer: null,
            touchStartPos: null,
            potentialHandle: null,
            isLongPressActive: false,
          });
        }
      } else if (selectedTool === 'move' && annotationsVisible) {
        // First, check if touching near any control handle (sensitivity: larger hit radius for tablet)
        const handleHit = findAnyControlHandleAt(touchPos, TOUCH_HANDLE_HIT_RADIUS);

        if (handleHit) {
          // Start long-press timer for vertex editing
          const timer = setTimeout(() => {
            // Long press confirmed - start editing the handle
            if (onShapeSelect) {
              onShapeSelect(handleHit.shapeId);
            }
            if (onDragStart) {
              onDragStart();
            }
            setEditState({
              isEditing: true,
              editingShapeId: handleHit.shapeId,
              editingHandle: handleHit.handleName,
            });
            setTouchEditState((prev) => ({
              ...prev,
              isLongPressActive: true,
              longPressTimer: null,
            }));
          }, LONG_PRESS_DURATION);

          setTouchEditState({
            longPressTimer: timer,
            touchStartPos: touchPos,
            potentialHandle: handleHit,
            isLongPressActive: false,
          });

          // Select the shape immediately to show handles
          if (onShapeSelect) {
            onShapeSelect(handleHit.shapeId);
          }
        } else {
          // No handle hit – selection-state cycling for inner structures (e.g. thalamus under HC)
          const allAtPoint = findAllShapesAtPoint(imagePos);
          let hitShape: Shape | null = null;
          if (allAtPoint.length > 0) {
            const selectedId = shapes.find(s => s.isSelected)?.id ?? null;
            const currentIdx = selectedId ? allAtPoint.findIndex(s => s.id === selectedId) : -1;
            hitShape = currentIdx >= 0
              ? allAtPoint[(currentIdx + 1) % allAtPoint.length]
              : allAtPoint[0];
          }

          if (hitShape) {
            // Start long-press timer for shape dragging
            const timer = setTimeout(() => {
              // Long press confirmed - start dragging
              if (onDragStart) {
                onDragStart();
              }
              setDragState({
                isDragging: true,
                draggedShapeId: hitShape.id,
                lastMousePos: imagePos,
              });
              setTouchEditState((prev) => ({
                ...prev,
                isLongPressActive: true,
                longPressTimer: null,
              }));
            }, LONG_PRESS_DURATION);

            setTouchEditState({
              longPressTimer: timer,
              touchStartPos: touchPos,
              potentialHandle: null,
              isLongPressActive: false,
            });

            if (onShapeSelect) {
              onShapeSelect(hitShape.id);
            }
          } else {
            if (onShapeSelect) {
              onShapeSelect(null);
            }
            setTouchEditState({
              longPressTimer: null,
              touchStartPos: null,
              potentialHandle: null,
              isLongPressActive: false,
            });
          }
        }
      } else {
        // Handle drawing start or ruler measurement
        const hitShape = findShapeAtPoint(imagePos);

        // Clear touch edit state for drawing modes
        setTouchEditState({
          longPressTimer: null,
          touchStartPos: null,
          potentialHandle: null,
          isLongPressActive: false,
        });

        if (!hitShape) {
          if (selectedTool === 'ruler') {
            setRulerState({
              isMeasuring: true,
              startPoint: imagePos,
              endPoint: imagePos,
            });
          } else if (annotationsVisible && isEditMode) {
            if (selectedTool === 'freehand') {
              setDrawingState({
                isDrawing: true,
                startPoint: imagePos,
                currentShape: {
                  type: 'freehand',
                  points: [imagePos],
                },
              });
            } else if (['line', 'circle'].includes(selectedTool)) {
              // Line and ellipse: single drag from start to end
              setDrawingState({ isDrawing: true, startPoint: imagePos });
            }
          }
        }
      }
    },
    [
      selectedTool,
      getTouchPos,
      imageScale,
      findShapeAtPoint,
      findAllShapesAtPoint,
      findAnyControlHandleAt,
      onShapeSelect,
      onDragStart,
      onShapeDelete,
      shapes,
      hoverState,
      annotationsVisible,
      isEditMode,
      hideContextMenu,
      drawingState,
      rulerState,
      touchEditState.longPressTimer,
    ]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      // Only handle single touch
      if (e.touches.length !== 1) return;

      const touchPos = getTouchPos(e);
      const imagePos = canvasToImageCoordinates(touchPos, imageScale);
      setCurrentMousePos(imagePos);

      // Check if we should cancel the long-press timer (finger moved too much)
      if (touchEditState.longPressTimer && touchEditState.touchStartPos) {
        const moveDistance = Math.sqrt(
          Math.pow(touchPos.x - touchEditState.touchStartPos.x, 2) +
            Math.pow(touchPos.y - touchEditState.touchStartPos.y, 2)
        );

        if (moveDistance > TOUCH_MOVE_THRESHOLD) {
          // Cancel long-press - finger moved too much
          clearTimeout(touchEditState.longPressTimer);
          setTouchEditState({
            longPressTimer: null,
            touchStartPos: null,
            potentialHandle: null,
            isLongPressActive: false,
          });
        }
      }

      // Handle panning (use ref so first touchmove works; pan only moves view, never shapes)
      const touchPanRef = panningRef.current;
      const touchIsPanning = touchPanRef ?? (panState.isPanning && panState.lastPanPos);
      const touchLastPanPos = touchPanRef?.lastPanPos ?? panState.lastPanPos;
      if (touchIsPanning && touchLastPanPos) {
        const deltaX = (touchPos.x - touchLastPanPos.x) * TOUCH_PAN_SENSITIVITY;
        const deltaY = (touchPos.y - touchLastPanPos.y) * TOUCH_PAN_SENSITIVITY;
        if (touchPanRef) touchPanRef.lastPanPos = touchPos;

        setImageScale((prev) => {
          const newPanX = prev.panX + deltaX;
          const newPanY = prev.panY + deltaY;

          const canvas = canvasRef.current;
          if (!canvas) return prev;

          const effectiveScaleX = prev.scaleX * prev.zoomLevel;
          const effectiveScaleY = prev.scaleY * prev.zoomLevel;
          const scaledWidth = prev.imageWidth * effectiveScaleX;
          const scaledHeight = prev.imageHeight * effectiveScaleY;

          const maxPanX = Math.max(0, scaledWidth - canvas.width) / 2;
          const maxPanY = Math.max(0, scaledHeight - canvas.height) / 2;

          const constrainedPanX = Math.max(
            -maxPanX,
            Math.min(maxPanX, newPanX)
          );
          const constrainedPanY = Math.max(
            -maxPanY,
            Math.min(maxPanY, newPanY)
          );

          return {
            ...prev,
            panX: constrainedPanX,
            panY: constrainedPanY,
          };
        });

        setPanState((prev) => ({
          ...prev,
          lastPanPos: touchPos,
        }));
        return;
      }

      // Handle shape handle editing (after long-press activated)
      if (
        editState.isEditing &&
        editState.editingShapeId &&
        editState.editingHandle &&
        onShapeUpdate
      ) {
        const editingShape = shapes.find(
          (s) => s.id === editState.editingShapeId
        );
        if (editingShape) {
          const updatedShape = updateShapeHandle(
            editingShape,
            editState.editingHandle,
            imagePos
          );
          onShapeUpdate(editState.editingShapeId, updatedShape);
        }
        return;
      }

      // Handle shape dragging (after long-press activated)
      if (
        dragState.isDragging &&
        dragState.draggedShapeId &&
        dragState.lastMousePos &&
        onShapeUpdate
      ) {
        const deltaX = imagePos.x - dragState.lastMousePos.x;
        const deltaY = imagePos.y - dragState.lastMousePos.y;

        const draggedShape = shapes.find(
          (s) => s.id === dragState.draggedShapeId
        );
        if (draggedShape) {
          const movedShape = moveShape(draggedShape, deltaX, deltaY);
          onShapeUpdate(dragState.draggedShapeId, movedShape);
        }

        setDragState((prev) => ({
          ...prev,
          lastMousePos: imagePos,
        }));
        return;
      }

      // Handle ruler measurement update
      if (rulerState.isMeasuring && rulerState.startPoint) {
        setRulerState((prev) => ({
          ...prev,
          endPoint: imagePos,
        }));
      }

      // Handle freehand drawing
      if (
        selectedTool === 'freehand' &&
        drawingState.isDrawing &&
        drawingState.currentShape &&
        drawingState.currentShape.type === 'freehand'
      ) {
        const freehandShape = drawingState.currentShape as any;
        const lastPoint = freehandShape.points[freehandShape.points.length - 1];

        const distance = Math.sqrt(
          Math.pow(imagePos.x - lastPoint.x, 2) +
            Math.pow(imagePos.y - lastPoint.y, 2)
        );

        if (distance > 1.5) {
          const newPoints = [...freehandShape.points, imagePos];
          setDrawingState({
            ...drawingState,
            currentShape: {
              ...freehandShape,
              points: newPoints,
            },
          });
        }
      }

      // Handle drawing preview
      if (drawingState.isDrawing && drawingState.startPoint) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        redrawCanvas();
        drawPreview(ctx, drawingState.startPoint, imagePos, selectedTool);
      }
    },
    [
      drawingState,
      dragState,
      panState,
      editState,
      touchEditState,
      selectedTool,
      getTouchPos,
      imageScale,
      redrawCanvas,
      drawPreview,
      shapes,
      onShapeUpdate,
      annotationsVisible,
      rulerState,
    ]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      // Clear any pending long-press timer
      if (touchEditState.longPressTimer) {
        clearTimeout(touchEditState.longPressTimer);
      }

      // Reset touch edit state
      setTouchEditState({
        longPressTimer: null,
        touchStartPos: null,
        potentialHandle: null,
        isLongPressActive: false,
      });

      // Handle pan end (clear ref)
      if (panState.isPanning || panningRef.current) {
        panningRef.current = null;
        setPanState({
          isPanning: false,
          lastPanPos: null,
        });
        return;
      }

      // Handle edit end
      if (editState.isEditing) {
        if (onDragEnd) {
          onDragEnd();
        }
        setEditState({
          isEditing: false,
          editingShapeId: null,
          editingHandle: null,
        });
        return;
      }

      // Handle drag end – keep selection so next tap on overlap can cycle to inner shape
      if (dragState.isDragging) {
        if (onDragEnd) {
          onDragEnd();
        }
        setDragState({
          isDragging: false,
          draggedShapeId: null,
          lastMousePos: null,
        });
        return;
      }

      // Handle ruler measurement end
      if (rulerState.isMeasuring) {
        setRulerState((prev) => ({
          ...prev,
          isMeasuring: false,
        }));
        return;
      }

      // Handle drawing completion
      if (drawingState.isDrawing && drawingState.startPoint) {
        // Get the last known position from currentMousePos
        const imagePos = currentMousePos || drawingState.startPoint;

        let newShape: Omit<Shape, 'id'> | null = null;

        switch (selectedTool) {
          case 'line':
            newShape = {
              type: 'line' as const,
              startPoint: drawingState.startPoint,
              endPoint: imagePos,
            } as Omit<Shape, 'id'>;
            break;

          case 'circle': {
            const sx = drawingState.startPoint.x;
            const sy = drawingState.startPoint.y;
            const ex = imagePos.x;
            const ey = imagePos.y;
            const cx = (sx + ex) / 2;
            const cy = (sy + ey) / 2;
            const radiusX = Math.max(Math.abs(ex - sx) / 2, 1);
            const radiusY = Math.max(Math.abs(ey - sy) / 2, 1);
            newShape = {
              type: 'circle' as const,
              center: { x: cx, y: cy },
              radiusX,
              radiusY,
              rotation: 0,
            } as Omit<Shape, 'id'>;
            break;
          }

          case 'square':
            newShape = {
              type: 'square' as const,
              startPoint: drawingState.startPoint,
              endPoint: imagePos,
            } as Omit<Shape, 'id'>;
            break;

          case 'freehand':
            if (
              drawingState.currentShape &&
              drawingState.currentShape.type === 'freehand'
            ) {
              const freehandShape = drawingState.currentShape as any;
              if (freehandShape.points.length >= 3) {
                const isAlreadyClosed = isFreehandShapeClosed(
                  freehandShape.points
                );
                const finalPoints = isAlreadyClosed
                  ? freehandShape.points
                  : [...freehandShape.points, freehandShape.points[0]];

                newShape = {
                  type: 'freehand' as const,
                  points: simplifyFreehandPoints(finalPoints),
                } as Omit<Shape, 'id'>;
              } else if (freehandShape.points.length >= 2) {
                newShape = {
                  type: 'freehand' as const,
                  points: simplifyFreehandPoints(freehandShape.points),
                } as Omit<Shape, 'id'>;
              }
            }
            break;
        }

        if (newShape && onShapeAdd) {
          // Apply preset label/color if active (match mouse handler)
          if (presetLabel) {
            onShapeAdd({ ...newShape, label: presetLabel, color: presetColor });
          } else {
            onShapeAdd(newShape);
          }
        }

        setDrawingState({ isDrawing: false });
      }
    },
    [
      drawingState,
      dragState,
      panState,
      editState,
      touchEditState,
      selectedTool,
      onShapeAdd,
      onShapeSelect,
      onDragEnd,
      rulerState,
      currentMousePos,
      presetLabel,
      presetColor,
    ]
  );

  // Redraw when shapes change or image loads
  useEffect(() => {
    if (loadedImage && imageScale.scaleX > 0) {
      redrawCanvas();
    }
  }, [loadedImage, imageScale, shapes, redrawCanvas]);

  // Redraw with preview when drawing state changes
  useEffect(() => {
    if (drawingState.isDrawing && drawingState.startPoint && currentMousePos) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      redrawCanvas();
      drawPreview(ctx, drawingState.startPoint, currentMousePos, selectedTool);
    }
  }, [drawingState, currentMousePos, selectedTool, redrawCanvas, drawPreview]);

  // Redraw with ruler measurement when ruler state changes
  useEffect(() => {
    if (
      rulerState.isMeasuring &&
      rulerState.startPoint &&
      rulerState.endPoint
    ) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      redrawCanvas();

      // Draw ruler line
      const startCanvas = imageToCanvasCoordinates(
        rulerState.startPoint,
        imageScale
      );
      const endCanvas = imageToCanvasCoordinates(
        rulerState.endPoint,
        imageScale
      );

      ctx.save();
      ctx.strokeStyle = '#f59e0b'; // Amber color for ruler
      ctx.lineWidth = 2.5;
      ctx.setLineDash([8, 4]); // Dashed line

      ctx.beginPath();
      ctx.moveTo(startCanvas.x, startCanvas.y);
      ctx.lineTo(endCanvas.x, endCanvas.y);
      ctx.stroke();

      // Draw end points
      ctx.setLineDash([]);
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(startCanvas.x, startCanvas.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(endCanvas.x, endCanvas.y, 4, 0, Math.PI * 2);
      ctx.fill();

      // Calculate and display measurement (in cm if pixel spacing available)
      const length = calculateLineLength(
        rulerState.startPoint,
        rulerState.endPoint,
        pixelSpacing
      );
      const unit = pixelSpacing ? 'cm' : 'px';
      const measurementText = formatMeasurement(length, unit);

      // Draw measurement label
      const midX = (startCanvas.x + endCanvas.x) / 2;
      const midY = (startCanvas.y + endCanvas.y) / 2;

      // Fixed font size (doesn't change with zoom)
      const rulerFontSize = 13;

      ctx.font = `bold ${rulerFontSize}px Inter, system-ui, sans-serif`;
      const textMetrics = ctx.measureText(measurementText);
      const padding = 8;
      const boxHeight = rulerFontSize + 8;
      const borderRadius = 6;

      // Draw rounded background
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(245, 158, 11, 0.95)'; // Amber background
      const bgX = midX - textMetrics.width / 2 - padding;
      const bgY = midY - boxHeight / 2;
      const bgW = textMetrics.width + padding * 2;
      const bgH = boxHeight;
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgW, bgH, borderRadius);
      ctx.fill();

      // Draw border
      ctx.strokeStyle = '#d97706';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Draw text with shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(measurementText, midX, midY);

      ctx.restore();
    }
  }, [rulerState, imageScale, redrawCanvas, pixelSpacing]);

  // Clear ruler measurement when tool changes
  useEffect(() => {
    if (
      selectedTool !== 'ruler' &&
      (rulerState.startPoint || rulerState.endPoint)
    ) {
      setRulerState({
        isMeasuring: false,
      });
    }
  }, [selectedTool, rulerState.startPoint, rulerState.endPoint]);

  // When switching away from a drawing tool (e.g. to Edit or Pan), clear any in-progress
  // drawing (e.g. circle after 1 or 2 clicks) so we don't leave stale state that could
  // cause the next click to behave incorrectly or make a completed circle appear to "disappear".
  const drawingTools = ['line', 'circle', 'freehand', 'square'];
  useEffect(() => {
    const isNowDrawingTool = drawingTools.includes(selectedTool);
    if (!isNowDrawingTool) {
      setDrawingState((prev) => (prev.isDrawing ? { isDrawing: false } : prev));
    }
  }, [selectedTool]);

  // Handle click outside to close context menu
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [contextMenu.visible, hideContextMenu]);

  // Handle wheel for image navigation with throttling.
  // Always attach to canvasRef: in video mode the canvas has pointer-events:auto and covers
  // the video content rect, which is where the user draws and scrolls.
  useEffect(() => {
    const target: Element | null = canvasRef.current;
    if (!target) return;

    let throttleTimeout: NodeJS.Timeout | null = null;
    const THROTTLE_DELAY = 300; // ms between frame changes

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (throttleTimeout) return;
      if (e.deltaY > 0) {
        if (onNextImage) onNextImage();
      } else if (e.deltaY < 0) {
        if (onPreviousImage) onPreviousImage();
      }
      throttleTimeout = setTimeout(() => { throttleTimeout = null; }, THROTTLE_DELAY);
    };

    target.addEventListener('wheel', handleWheel, { passive: false });

    // Prevent pinch-to-zoom on trackpad/touch devices
    const handleTouchStart = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };
    const handleTouchMove  = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };

    target.addEventListener('touchstart', handleTouchStart, { passive: false });
    target.addEventListener('touchmove',  handleTouchMove,  { passive: false });

    return () => {
      target.removeEventListener('wheel', handleWheel);
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchmove',  handleTouchMove);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [onNextImage, onPreviousImage]);

  // Determine cursor style
  const getCursorStyle = useCallback(() => {
    if (panState.isPanning) {
      return 'grabbing';
    }
    if (editState.isEditing) {
      return 'grabbing';
    }
    if (dragState.isDragging) {
      return 'grabbing';
    }
    if (selectedTool === 'move' && annotationsVisible) {
      if (hoverState.hoveredHandle) {
        return 'pointer'; // Show pointer cursor when hovering over handles
      }
      if (hoverState.hoveredShapeId) {
        return 'grab'; // Show grab cursor when hovering over shapes
      }
      return 'default'; // Default cursor when not hovering anything
    }
    if (selectedTool === 'pan' && annotationsVisible) {
      return hoverState.hoveredShapeId ? 'grab' : 'default';
    }
    if (selectedTool === 'delete' && annotationsVisible) {
      return 'pointer'; // Hand cursor in delete mode (over shape or not)
    }
    if (annotationsVisible) {
      // If context menu is visible, use default cursor to indicate drawing is disabled
      if (contextMenu.visible) {
        return 'default';
      }
      // For drawing tools, use pointer cursor when hovering over existing shapes (indicates interaction)
      if (
        ['line', 'circle', 'freehand', 'square'].includes(selectedTool) &&
        hoverState.hoveredShapeId
      ) {
        return 'pointer';
      }
      return 'crosshair';
    }
    return 'default'; // Default cursor when annotations are hidden
  }, [
    selectedTool,
    dragState.isDragging,
    panState.isPanning,
    editState.isEditing,
    hoverState,
    annotationsVisible,
    contextMenu.visible,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(videoMode ? { pointerEvents: 'none' } : {}),
      }}
    >
      <canvas
        ref={setCanvasRef}
        width={canvasSize.width || width}
        height={canvasSize.height || height}
        className={`image-canvas${videoMode ? ' image-canvas-video-mode' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: getCursorStyle(), touchAction: 'none' }}
      />

      {/* Context Menu */}
      {/* {contextMenu.visible && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
            padding: '6px',
            zIndex: 1000,
            minWidth: '180px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onMouseDown={(e) => e.stopPropagation()} // Prevent canvas mouse events
        >
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleToggleLabelMenu()
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: '6px',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path>
                  <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2"></path>
                  <path d="M7 13h10"></path>
                </svg>
                {shapes.find(s => s.id === contextMenu.shapeId)?.label ? 'Edit Label' : 'Add Label'}
              </div>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ 
                  transform: contextMenu.showLabelMenu ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>

            {contextMenu.showLabelMenu && (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  marginLeft: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
                  padding: '6px',
                  minWidth: '280px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1001
                }}
              >
                {LABEL_CATEGORIES.map(category => {
                  const categoryLabels = FETAL_ANNOTATION_LABELS.filter(l => l.category === category.value)
                  if (categoryLabels.length === 0) return null
                  
                  return (
                    <div key={category.value} style={{ marginBottom: '8px' }}>
                      <div style={{
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#666',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {category.label}
                      </div>
                      {categoryLabels.map(labelOption => (
                        <button
                          key={labelOption.value}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleLabelSelect(labelOption.label)
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            backgroundColor: shapes.find(s => s.id === contextMenu.shapeId)?.label === labelOption.label ? '#e6f2ff' : 'transparent',
                            borderRadius: '6px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: shapes.find(s => s.id === contextMenu.shapeId)?.label === labelOption.label ? '#007acc' : '#333',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (shapes.find(s => s.id === contextMenu.shapeId)?.label !== labelOption.label) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (shapes.find(s => s.id === contextMenu.shapeId)?.label !== labelOption.label) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }
                          }}
                        >
                          {labelOption.label}
                        </button>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div style={{ 
            height: '1px', 
            backgroundColor: '#e0e0e0', 
            margin: '6px 0' 
          }} />

          <button
            onClick={(e) => {
              e.stopPropagation()
              handleContextMenuDelete()
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: '6px',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: '#dc3545',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#fff5f5'
              e.currentTarget.style.color = '#b02a37'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#dc3545'
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="3,6 5,6 21,6"></polyline>
              <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            Delete Annotation
          </button>
        </div>
      )} */}
    </div>
  );
}
